import React, { useEffect, useRef } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import ImageLayer from 'ol/layer/Image';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import ImageWMS from 'ol/source/ImageWMS';
import { fromLonLat } from 'ol/proj';
import { defaults as defaultControls } from 'ol/control';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import { useMapStore } from '../../store/map.store';
import { useMapInteractions, resetInteractionRefs } from '../../hooks/useMapInteractions';
import { resetLayerFlags } from '../../hooks/useLayers';
import { getMapColors } from '../../themes/mapTheme';
import { geoserverEndpoint } from '@/utils/geoserverAccess';
import { useAuth } from '@/components/Auth/AuthProvider';
import { companyHomeView } from './companyView';
import { CompanyLogo } from './CompanyLogo';
import { usePlatformLayerViews } from '@/utils/platformViews';

interface MapViewProps {
  onMapInitialized?: (map: Map) => void;
}

/** Build layer colour array from the active theme palette. */
function getLayerColors(): string[] {
  const mc = getMapColors();
  return [
    ...mc.walk,       // 0-4
    ...mc.drive,      // 5-9
    ...mc.motor,      // 10-14
    mc.selection,     // 15
    mc.ringBuffer,    // 16
    mc.highlight,     // 17
  ];
}

export const MapView: React.FC<MapViewProps> = ({ onMapInitialized }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const { setMap, resetMap } = useMapStore();
  // our layers drawn with the person's own views of them (GXV), as GeoServerProxy makes them
  usePlatformLayerViews();
  const mapInstance = useMapStore((s) => s.mapInstance);
  const setHomeView = useMapStore((s) => s.setHomeView);
  const { user } = useAuth();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const appliedHomeFor = useRef<string | null>(null);

  useMapInteractions();

  useEffect(() => {
    if (!mapRef.current) return;

    // Create base layers
    const roadLayer = new TileLayer({
      source: new OSM(),
      properties: { name: 'base-road' },
      visible: true,
    });

    const satelliteLayer = new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        maxZoom: 19,
      }),
      properties: { name: 'base-satellite' },
      visible: false,
    });

    const hybridLabelLayer = new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
        maxZoom: 19,
      }),
      properties: { name: 'base-hybrid-labels' },
      visible: false,
    });

    const terrainLayer = new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
        maxZoom: 19,
      }),
      properties: { name: 'base-terrain' },
      visible: false,
    });

    const grayLayer = new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
        maxZoom: 16,
      }),
      properties: { name: 'base-gray' },
      visible: false,
    });

    // Placeholder WMS layer (real layers added dynamically via useLayers)
    const wmsSource = new ImageWMS({
      url: geoserverEndpoint('cite', 'wms'),
      params: {
        LAYERS: 'cite:boundaries',
        FORMAT: 'image/png',
        TRANSPARENT: true,
      },
      serverType: 'geoserver',
      ratio: 1,
    });

    const wmsLayer = new ImageLayer({
      source: wmsSource,
      properties: { name: 'wms-layer' },
      visible: false,
    });

    // Create configuration/analysis vector layers
    const configLayers = createConfigLayers();

    // Create the map — disable default controls (we have custom ones)
    const map = new Map({
      target: mapRef.current,
      controls: defaultControls({
        zoom: false,
        rotate: false,
        attribution: false,
      }),
      layers: [roadLayer, satelliteLayer, hybridLabelLayer, terrainLayer, grayLayer, wmsLayer, ...configLayers],
      view: new View({
        projection: 'EPSG:3857',
        center: fromLonLat([29.0, 41.0], 'EPSG:3857'),
        zoom: 11,
      }),
    });

    // Store map in Zustand
    setMap(map);

    if (onMapInitialized) {
      onMapInitialized(map);
    }

    // Handle zoom changes: only record the view's zoom in the store. Writing it back into the view
    // (setZoom) cancels every animation in progress, e.g. the fit to a searched address.
    const handleZoomChange = () => {
      const zoom = map.getView().getZoom();
      if (zoom !== undefined) {
        useMapStore.setState({ zoom });
      }
    };

    map.getView().on('change:resolution', handleZoomChange);

    // Handle map resize
    const handleResize = () => {
      map.updateSize();
    };

    resizeObserverRef.current = new ResizeObserver(handleResize);
    resizeObserverRef.current.observe(mapRef.current);

    // Handle map clicks for info tool
    map.on('click', (_evt) => {
      const view = map.getView();
      const resolution = view.getResolution();
      if (!resolution) return;

      // TODO: Implement GetFeatureInfo query for active tool
    });

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      map.getView().un('change:resolution', handleZoomChange);
      map.dispose();
      resetMap();
      resetLayerFlags();
      resetInteractionRefs();
    };
  }, [setMap, resetMap, onMapInitialized]);

  // The company's start view (Admin → Companies: latitude, longitude, zoom), once per map and company.
  const home = companyHomeView(user);
  const homeKey = home ? `${home.center[0]},${home.center[1]},${home.zoom}` : 'none';
  useEffect(() => {
    setHomeView(home);
    if (!mapInstance || !home) return;
    const key = `${(mapInstance as any).ol_uid ?? ''}|${homeKey}`;
    if (appliedHomeFor.current === key) return;
    appliedHomeFor.current = key;
    const view = mapInstance.getView();
    view.cancelAnimations();
    view.setCenter(fromLonLat(home.center, 'EPSG:3857'));
    view.setZoom(home.zoom);
  }, [mapInstance, homeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div
        ref={mapRef}
        className="w-full h-full"
        style={{ position: 'absolute', top: 0, left: 0 }}
      />
      <CompanyLogo />
    </>
  );
};

function createConfigLayers(): VectorLayer<any>[] {
  const layers: VectorLayer<any>[] = [];

  // Create 18 configuration layers with proper styling
  const layerConfigs = [
    // Walk-in layers (5)
    { name: 'walk1', type: 'walk', index: 0 },
    { name: 'walk2', type: 'walk', index: 1 },
    { name: 'walk3', type: 'walk', index: 2 },
    { name: 'walk4', type: 'walk', index: 3 },
    { name: 'walk5', type: 'walk', index: 4 },
    // Drive layers (5)
    { name: 'drive1', type: 'drive', index: 5 },
    { name: 'drive2', type: 'drive', index: 6 },
    { name: 'drive3', type: 'drive', index: 7 },
    { name: 'drive4', type: 'drive', index: 8 },
    { name: 'drive5', type: 'drive', index: 9 },
    // Motor layers (5)
    { name: 'motor1', type: 'motor', index: 10 },
    { name: 'motor2', type: 'motor', index: 11 },
    { name: 'motor3', type: 'motor', index: 12 },
    { name: 'motor4', type: 'motor', index: 13 },
    { name: 'motor5', type: 'motor', index: 14 },
    // Special layers
    { name: 'dispConfLayer', type: 'special', index: 15 },
    { name: 'RingBuffer', type: 'special', index: 16 },
    { name: 'editLayer', type: 'special', index: 17 },
  ];

  layerConfigs.forEach((config) => {
    const source = new VectorSource();
    const color = getLayerColors()[config.index];

    const layer = new VectorLayer({
      source,
      properties: { name: config.name },
      style: new Style({
        fill: new Fill({
          color: color + '40', // 40 = 25% opacity in hex
        }),
        stroke: new Stroke({
          color: color,
          width: 2,
        }),
      }),
      visible: false,
    });

    layers.push(layer);
  });

  return layers;
}
